import lodash from 'lodash';
import moment, { MomentInput } from 'moment';
import { DATE_FORMAT, CONTRIBUTION_LEVELS, EVALUATION_STATUSES, STATUS_TYPES, ALL_TIME_VALUE } from '@/constants';
import { ContributionLevel, DisplayStatus, SelectionOptions, TreeOption } from '@/models';

export * from './peer-evaluation';
export * from './user-engagement';
export * from './unit-test';

export const formatDateToLocalDateTime = (date: string): string => {
  return moment.utc(date).toISOString();
};

export const formatDateToDisplay = (isoString: string): string => {
  if (!isoString) {
    return '-';
  }
  return moment(isoString || '').format(DATE_FORMAT.DISPLAY);
};

export const formatDateToVTextField = (date: string): string => {
  if (date) {
    return moment.utc(date).toISOString().substr(0, 10);
  }
  return '';
};

export const convertNumberToFourDigitsString = (num: number): string => {
  if (num) {
    if (num < 1000) {
      return ('000' + num).toString().slice(-4);
    }
    return '' + num;
  }
  return '####';
};

export const sumByField = <T>(arr: Array<T>, field: keyof T): number => {
  return arr.reduce((acc, curr) => acc + +curr[field], 0);
};

export const averageByField = <T>(arr: Array<T>, field: keyof T): number => {
  return arr.reduce((acc, curr) => acc + +curr[field], 0) / arr.length;
};

export const getDecimalParts = (num: number): number => {
  return num - Math.floor(num);
};

export const sortArrayByDateField = <T>(arr: Array<T>, field: keyof T): Array<T> => {
  return arr.sort((item1, item2) => {
    if (item1[field] === item2[field]) {
      return 0;
    }
    return moment(item1[field] as MomentInput).isBefore(item2[field] as MomentInput) ? 1 : -1;
  });
};

const allowNumberInputList = [
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  'Backspace',
  'Tab',
  'ArrowLeft',
  'ArrowRight'
];

export const isValidNumericInputKey = (event: KeyboardEvent): boolean => {
  const allowList = allowNumberInputList.concat(['.']);
  return allowList.includes(event.key);
};

export const isValidNotDecimalInputKey = (event: KeyboardEvent): boolean => {
  return allowNumberInputList.includes(event.key);
};

export const getContributionLevelFromDrops = (drops: number): ContributionLevel => {
  const levels: ContributionLevel[] = Object.values(CONTRIBUTION_LEVELS);
  const currentLevel = levels.find(level => level.min <= drops && drops <= level.max);
  return currentLevel as ContributionLevel;
};

export const getNextContributionLevelFromDrops = (drops: number): ContributionLevel | null => {
  const levels: ContributionLevel[] = Object.values(CONTRIBUTION_LEVELS);
  const currentLevelIdx = levels.findIndex(level => level.min <= drops && drops <= level.max);
  if (currentLevelIdx === levels.length - 1) {
    return null;
  }
  return levels[currentLevelIdx + 1] as ContributionLevel;
};

export const getContributionLevelTextFromDrops = (drops: number): string => {
  const levels: ContributionLevel[] = Object.values(CONTRIBUTION_LEVELS);
  const currentLevel = levels.find(level => level.min <= drops && drops <= level.max);
  return lodash.replace(currentLevel?.name || 'No level', ' ', '_').toUpperCase();
};

export const getNumberOfSkipLevelByKey = (oldKey: string, newKey: string): number => {
  const levelKeys = Object.keys(CONTRIBUTION_LEVELS);
  const oldLevelIndex = levelKeys.findIndex(key => key === oldKey);
  const newLevelIndex = levelKeys.findIndex(key => key === newKey);
  return newLevelIndex - oldLevelIndex;
};

export const displayPrizeType = (type: string): string => {
  switch (type) {
    case 'VOUCHER':
      return 'Voucher';
    case 'BACKPACK':
      return 'Backpack';
    case 'FOOD':
      return 'Food';
    case 'COMPUTER_DEVICE':
      return 'Computer device';
    case 'HOUSEWARE':
      return 'Houseware';
    case 'HEALTHCARE':
      return 'Healthcare';
    case 'ELECTRONIC_DEVICE':
      return 'Electronic device';
    default:
      return 'Other';
  }
};

export const tableHeight = (pageCount: number): string => {
  return pageCount > 1 ? '70vh' : '100%';
};

export const formatCurrency = (amount: number): string => {
  return amount.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' });
};

export const sortArrayByStatusField = <T>(arr: Array<T>, field: keyof T, statusType: string): Array<T> => {
  switch (statusType) {
    case STATUS_TYPES.EVALUATION: {
      return arr.sort((item1, item2) => {
        const { sortValue: sortValue1 = 0 } = EVALUATION_STATUSES[item1[field] as unknown as keyof DisplayStatus] || {
          sortValue: 0
        };
        const { sortValue: sortValue2 = 0 } = EVALUATION_STATUSES[item2[field] as unknown as keyof DisplayStatus] || {
          sortValue: 0
        };
        if (sortValue1 === sortValue2) {
          return 0;
        }
        return sortValue1 < sortValue2 ? 1 : -1;
      });
    }
    default: {
      return arr;
    }
  }
};

export const getDisplayQuarterByDate = (dateString: string): string => {
  if (!dateString) {
    return '';
  }
  const thatDate = new Date(dateString);
  const quarter = Math.ceil((thatDate.getMonth() + 1) / 3);
  const year = thatDate.getFullYear();
  return `Q${quarter}-${year}`;
};

export const getDisplayQuarterBetweenDate = (startDate: string, endDate: string): string => {
  if (!startDate || !endDate) {
    return '';
  }
  const startDateDate = new Date(startDate);
  const startQuarter = Math.ceil((startDateDate.getMonth() + 1) / 3);
  const startYear = startDateDate.getFullYear();
  const endDateDate = new Date(endDate);
  const endQuarter = Math.ceil((endDateDate.getMonth() + 1) / 3);
  const endYear = endDateDate.getFullYear();
  if (startYear === endYear) {
    if (startQuarter === endQuarter) {
      return `Q${startQuarter}-${startYear}`;
    }
    return `Q${startQuarter} & Q${endQuarter}-${startYear}`;
  }
  return `Q${startQuarter}-${startYear} & Q${endQuarter}-${endYear}`;
};

export const toMoneyDisplay = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num);
};

export const toTreeOptionsByQuarters = (quarters: string[]): TreeOption[] => {
  const years = [...new Set(quarters.map(q => q.split('-')?.[1]?.trim()))];
  return years.map(y => {
    const children = quarters
      .filter(q => q.includes(y))
      .map(q => ({
        id: q,
        name: q
      }));
    return {
      id: y,
      name: y,
      children
    };
  });
};

export const toTreeOptionsByPeriods = (periods: string[]): TreeOption[] => {
  const years = [
    ...new Set(
      periods.map(q => {
        const temp = q.split('&');
        if (temp.length > 1) {
          const firstYear = temp[0].split('-')?.[1]?.trim();
          const secondYear = temp[1].split('-')?.[1]?.trim();
          if (firstYear && secondYear) {
            return `${firstYear}-${secondYear}`;
          }
          return firstYear || secondYear;
        }
        return q.split('-')?.[1]?.trim();
      })
    )
  ];
  return years.map(y => {
    const temp = y.split('-');
    const children = periods
      .filter(p => {
        if (temp.length > 1) {
          return p.includes(temp?.[0]?.trim()) && p.includes(temp?.[1]?.trim());
        }
        return p.split('-')?.[1]?.trim() === y;
      })
      .map(p => ({
        id: p,
        name: p
      }));
    return {
      id: y,
      name: y,
      children
    };
  });
};

export const extractQuarterFromName = (name: string): string => {
  const startIdx = name?.indexOf('Q');
  if (startIdx === -1) {
    return '';
  }
  const endIdx = name?.length;
  return name.slice(startIdx, endIdx).trim();
};

const defaultDayFormat = 'YYYY_MM_DD_h_ss_mm_a';
export const getCurrentDayString = (format = defaultDayFormat): string => {
  return moment().format(format);
};

export const toTreeOptionsByQuarterOptions = (quarterOptions: SelectionOptions, hasAllTime = true): TreeOption[] => {
  const years = [
    ...new Set(
      quarterOptions.map(q => {
        return q.text.split('-')?.[1]?.trim();
      })
    )
  ];
  const result = years.map(y => {
    const children = quarterOptions
      .filter(q => q.text.includes(y))
      .map(({ value, text }) => ({
        id: value,
        name: text
      }));
    return {
      id: y,
      name: y,
      children
    };
  });
  if (hasAllTime) {
    result.push({
      id: ALL_TIME_VALUE.toString(),
      name: 'All Time',
      children: []
    });
  }
  return result;
};

export const cleanSettings = (settings: Record<string, number> | undefined | null): Record<string, number> => {
  const clean: Record<string, number> = {};
  if (!settings) return clean;
  for (const key of Object.keys(settings)) {
    if (settings[key] > 0) {
      clean[key] = settings[key];
    }
  }
  return clean;
};
